import { useMsal } from '@azure/msal-react';
import { mergeClasses, SearchBox, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCommonStyles, useStores } from '../../common/hooks';
import { blankRegionLead, Country, ProgramCountryLead, ProgramCountryNominationReviewer, ProgramRegion, RegionLead } from '../../models';
import { PageTitle } from '../shared-components';
import { AssignDesignations } from '../shared-components/assign-designations/AssignDesignations';
import AssignEligibilityRules from '../shared-components/assign-eligibility-rules/AssignEligibilityRules';
import { FlexBox } from '../shared-components/flex-box';
import { LeadAssignment } from '../shared-components/lead-assignment/LeadAssignment';
import Loading from '../shared-components/Loading';
import RegionalCountriesAccordion from './components/RegionalCountriesAccordion';
import './EditRegionalPage.scss';

const EditRegionalPageImpl: React.FC = () => {
  const {
    instance,
  } = useMsal();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>();
  const [selectedRegion, setSelectedRegion] = useState<ProgramRegion>();
  const [searchText, setSearchText] = useState('');

  const {
    apiStore,
    awardStore,
    userStore,
    editRegionalPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();

  const handleAssignProgramCountryLead = async (emailAddress: string, isPrimary: boolean): Promise<void> => {
    setIsLoading(true);
    if (!awardStore.defaultProgramRef || !selectedCountry) {
      setIsLoading(false);
      return;
    }

    const newProgramCountryLead: ProgramCountryLead = {
      programCountryLeadId: 0,
      programId: awardStore.defaultProgramRef.programId,
      countryId: selectedCountry.countryId,
      emailAddress: emailAddress,
      isPrimary: isPrimary,
    }
    try {
      await apiStore.assignProgramCountryLead(instance, awardStore.defaultProgramRef.programId, selectedCountry.countryId, newProgramCountryLead);
      await getProgramCountryLeads(selectedCountry.countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteProgramCountryLead = async (programCountryLead: ProgramCountryLead): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteProgramCountryLead(instance, programCountryLead.programCountryLeadId);
      await getProgramCountryLeads(programCountryLead.countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteProgramCountryNominationReviewer = async (programCountryNominationReviewer: ProgramCountryNominationReviewer): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteProgramCountryNominationReviewer(instance, programCountryNominationReviewer.programCountryNominationReviewerId);
      await getProgramCountryNominationReviewers(programCountryNominationReviewer.countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleAssignProgramCountryNominationReviewer = async (emailAddress: string): Promise<void> => {
    setIsLoading(true);
    if (!awardStore.defaultProgramRef || !selectedCountry) {
      setIsLoading(false);
      return;
    }

    const newProgramCountryNominationReviewer: ProgramCountryNominationReviewer = {
      programCountryNominationReviewerId: 0,
      programId: awardStore.defaultProgramRef.programId,
      countryId: selectedCountry.countryId,
      emailAddress: emailAddress,
    }
    try {
      await apiStore.assignProgramCountryNominationReviewer(
        instance,
        awardStore.defaultProgramRef.programId,
        selectedCountry.countryId,
        newProgramCountryNominationReviewer
      );
      await getProgramCountryNominationReviewers(selectedCountry.countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleCountrySelected = async (country: Country): Promise<void> => {
    setIsLoading(true);
    setSelectedCountry(country);
    setSelectedRegion(undefined);

    await Promise.allSettled([
      getProgramCountryLeads(country.countryId),
      getProgramCountryNominationReviewers(country.countryId),
      userStore.isAdmin ? getCountryProgramEligibilityRules(country.countryId) : undefined
    ]);

    setIsLoading(false);
  };

  const addCountryProgramEligibilityRules = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    setIsLoading(true);
    try {
      await apiStore.addCountryProgramEligibilityRules(instance, awardStore.defaultProgramRef.programId, countryId, editRegionalPageUiStore.selectedEligibilityRuleCodes);
      await getCountryProgramEligibilityRules(countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignRegionLead = async (emailAddress: string, isPrimary: boolean): Promise<void> => {
    setIsLoading(true);
    if (!awardStore.defaultProgramRef || !selectedRegion) {
      setIsLoading(false);
      return;
    }

    const newRegionLead = blankRegionLead();
    newRegionLead.programId = awardStore.defaultProgramRef.programId;
    newRegionLead.regionId = selectedRegion.regionId;
    newRegionLead.emailAddress = emailAddress;
    newRegionLead.isPrimary = isPrimary;

    try {
      await apiStore.assignRegionLead(instance, newRegionLead.programId, selectedRegion.regionId, newRegionLead);
      await getProgramRegionLeads(selectedRegion.regionId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteRegionLead = async (regionLead: RegionLead): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteRegionLead(instance, regionLead.programRegionLeadId);
      await getProgramRegionLeads(regionLead.regionId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleRegionSelected = async (region: ProgramRegion) => {
    setIsLoading(true);

    setSelectedRegion(region);
    setSelectedCountry(undefined);

    await getProgramRegionLeads(region.regionId);

    setIsLoading(false);
  }

  const isDisableCountryLeadAssignment = (): boolean => {
    return (!selectedRegion && !selectedCountry) ||
      (selectedCountry !== undefined && !(userStore.isAdmin || userStore.isRegionLead)) ||
      (selectedRegion !== undefined && !(userStore.isAdmin));
  }

  const isDisableCountryNominationReviewerAssignment = (): boolean => {
    return (!selectedRegion && !selectedCountry) ||
      (selectedCountry !== undefined && !(userStore.isAdmin || userStore.isRegionLead || userStore.isCountryLead)) ||
      (selectedRegion !== undefined && !(userStore.isAdmin));
  };

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setIsLoading(true);
      try {
        await getProgramRegions(query);
      } catch (error) {
        // TODO - handle error
      } finally {
        setIsLoading(false);
      }
    },
    1000
  );

  const handleSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const handleDesignationsChange = async (updatedDesignations: string[]): Promise<void> => {
    if (!selectedCountry) return;

    editRegionalPageUiStore.handleOnDesignationChange(updatedDesignations);
    await addCountryProgramEligibilityRules(selectedCountry.countryId);
  };

  const getProgramRegions = async (searchText?: string) => {
    if (!awardStore.defaultProgramRef) return;

    try {
      const programRegions = await apiStore.getProgramRegions(instance, awardStore.defaultProgramRef.programId, searchText);
      editRegionalPageUiStore.setProgramRegions(programRegions);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getProgramRegionLeads = async (regionId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const regionLeads = await apiStore.getProgramRegionLeads(instance, awardStore.defaultProgramRef.programId, regionId);
      editRegionalPageUiStore.setRegionLeads(regionLeads);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getProgramCountryLeads = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const programCountryLeads = await apiStore.getProgramCountryLeads(instance, awardStore.defaultProgramRef.programId, countryId);
      editRegionalPageUiStore.setProgramCountryLeads(programCountryLeads);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getProgramCountryNominationReviewers = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const programCountryNominationReviewers = await apiStore.getProgramCountryNominationReviewers(instance, awardStore.defaultProgramRef.programId, countryId);
      editRegionalPageUiStore.setProgramCountryNominationReviewers(programCountryNominationReviewers);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getCountryProgramEligibilityRules = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const existingRules = await apiStore.getCountryProgramEligibilityRules(instance, awardStore.defaultProgramRef.programId, countryId);
      editRegionalPageUiStore.setEligibilityRuleCodes(existingRules);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getDesignations = async (): Promise<void> => {
    try {
      const designationOptions = await apiStore.getDesignationProgramOptions(instance);
      editRegionalPageUiStore.setDesignationOptions(designationOptions);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getEligibilityRules = async (): Promise<void> => {
    try {
      const rules = await apiStore.getEligibilityRules(instance, 'Country');
      awardStore.setEligibilityRules('Country', rules);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (userStore.isAdmin) {
      await getDesignations();
      await getEligibilityRules();
    }

    await getProgramRegions();
    setSelectedCountry(undefined);

    setIsLoading(false);
  }

  useEffect(() => {
    preloadData();
    return () => {
      editRegionalPageUiStore.resetStates();
    }
  }, []);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      className='edit-regional-page'
    >
      {isLoading && <Loading />}
      <PageTitle
        title='Edit regional and country leads'
        subtitle={awardStore.defaultProgramRef?.displayName}
      />
      <FlexBox
        isFullWidth
        className='edit-regional-page__main'
      >
        <div className='main-sidebar'>
          <div className='main-sidebar__search-box'>
            <SearchBox
              id='regional-search'
              placeholder='Search'
              value={searchText}
              className={mergeClasses(commonStyles.inputField, 'search-box__input')}
              onChange={(_, data) => handleSearchTextChanged(data.value)}
            />
          </div>
          <RegionalCountriesAccordion
            regions={editRegionalPageUiStore.programRegions}
            onCountrySelected={handleCountrySelected}
            onRegionSelected={handleRegionSelected}
          />
        </div>
        <FlexBox
          isFullWidth
          direction='column'
          className='main-panel'
        >
          <Text
            block
            as='h2'
            size={500}
            weight='semibold'
            className='main-panel__title'
          >
            {selectedCountry ? selectedCountry.displayName :
              selectedRegion ? selectedRegion.displayName : 'Region / Country'}
          </Text>
          <FlexBox
            aligns='start'
            direction='column'
            className='main-panel__content'
          >
            {
              selectedCountry &&
              <>
                <LeadAssignment
                  leads={editRegionalPageUiStore.programCountryLeads}
                  title='Primary award lead'
                  isPrimaryLead={true}
                  allowMultipleEmailAddresses={false}
                  onAddLead={emailAddress => handleAssignProgramCountryLead(emailAddress, true)}
                  onDeleteLead={lead => handleDeleteProgramCountryLead(lead as ProgramCountryLead)}
                  disabled={isDisableCountryLeadAssignment()}
                  requiresMicrosoftEmailAddress
                />
                <LeadAssignment
                  leads={editRegionalPageUiStore.programCountryLeads}
                  title='Award assistants'
                  isPrimaryLead={false}
                  allowMultipleEmailAddresses={false}
                  onAddLead={emailAddress => handleAssignProgramCountryLead(emailAddress, false)}
                  onDeleteLead={lead => handleDeleteProgramCountryLead(lead as ProgramCountryLead)}
                  disabled={isDisableCountryLeadAssignment()}
                  requiresMicrosoftEmailAddress
                />
                <LeadAssignment
                  leads={editRegionalPageUiStore.programCountryNominationReviewers}
                  isPrimaryLead={false}
                  title='Nomination Review Team Members'
                  allowMultipleEmailAddresses={false}
                  onAddLead={handleAssignProgramCountryNominationReviewer}
                  onDeleteLead={nominationReviewer => handleDeleteProgramCountryNominationReviewer(nominationReviewer as ProgramCountryNominationReviewer)}
                  disabled={isDisableCountryNominationReviewerAssignment()}
                  requiresMicrosoftEmailAddress
                />
                {userStore.isAdmin && (
                  <FlexBox
                    direction='column'
                    className='content-qualifications'
                  >
                    <Text
                      weight='semibold'
                      size={400}
                    >
                      Country Award Qualifications
                    </Text>
                    <AssignDesignations
                      label='Designations'
                      allDesignationCheckedText='Any Solutions Partner Designation'
                      designations={editRegionalPageUiStore.designationOptions}
                      selectedDesignationCodes={editRegionalPageUiStore.selectedEligibilityRuleCodes}
                      onDesignationsChange={handleDesignationsChange}
                    />
                    <AssignEligibilityRules
                      eligibilityRules={awardStore.getEligibilityRules('Country')}
                      selectedEligibilityRuleCodes={editRegionalPageUiStore.selectedEligibilityRuleCodes}
                      onEligibilityRulesChanged={() => addCountryProgramEligibilityRules(selectedCountry.countryId)}
                    />
                  </FlexBox>
                )}
              </>
            }
            {
              selectedRegion &&
              <LeadAssignment
                leads={editRegionalPageUiStore.regionLeads}
                title='Add regional leads'
                isPrimaryLead={false}
                allowMultipleEmailAddresses={false}
                onAddLead={emailAddress => handleAssignRegionLead(emailAddress, false)}
                onDeleteLead={lead => handleDeleteRegionLead(lead as RegionLead)}
                disabled={isDisableCountryLeadAssignment()}
                requiresMicrosoftEmailAddress
              />
            }
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
}

export const EditRegionalPage = observer(EditRegionalPageImpl);
